/**
 * Extracted Fields Display Component
 * Shows extracted/answered fields in scrollable view
 * Supports both view-only and edit modes
 */

import React, { useState, useEffect, useMemo } from 'react';
import Button from '@amzn/meridian/button';
import Text from '@amzn/meridian/text';
import Loader from '@amzn/meridian/loader';
import { Flipper, Flipped } from 'react-flip-toolkit';
import { useAppSelector } from '../../../store/store';
import { isFieldNotApplicable, isFieldExtracting, isFieldPending, isGroupedFieldValue, getMeaningfulGroupEntries, getFieldLabel, getFieldOrder, getFieldDefinition, getFieldGroup, sortOptionsSelectedFirst, fieldValuesEqual, getFieldFormatPlaceholder, validateFieldFormat, resolveFieldOptions, applyGroupedChildInit, FieldValue } from '../../../utils/fieldUtils';
import { useFieldChangeHighlight, HIGHLIGHT_PULSE_CSS, getHighlightStyle } from '../../../utils/animationUtils';
import { FIELD_BG_ANSWERED, FIELD_BG_NOT_SURE, FIELD_BG_UNANSWERED, FONT_SIZE_BODY, LINE_HEIGHT_BODY, TEXT_COLOR_PRIMARY, TEXT_COLOR_SECONDARY, FIELD_ICON_ANSWERED, FIELD_ICON_NOT_SURE, FIELD_ICON_UNANSWERED, TRANSITION_BG_COLOR } from '../cssConstants';
import EditFieldRenderer from './EditFieldRenderer';
import EvidenceModal from './EvidenceModal';

interface ExtractedFieldsDisplayProps {
  fieldNames?: string[];
  extractedFields: Partial<Record<string, FieldValue>>;
  editable?: boolean;
  isEditMode?: boolean;
  onSave?: (fields: Record<string, FieldValue>) => Promise<void>;
  onCancel?: () => void;
  onEdit?: () => void;
  isSaving?: boolean;
}

const ExtractedFieldsDisplay: React.FC<ExtractedFieldsDisplayProps> = ({
  fieldNames: fieldNamesProp,
  extractedFields,
  editable = false,
  isEditMode = false,
  onSave,
  onCancel,
  onEdit,
  isSaving = false
}) => {
  const [editedFields, setEditedFields] = useState<Partial<Record<string, FieldValue>>>(extractedFields);
  const [searchQueries, setSearchQueries] = useState<Record<string, string>>({});
  const [evidenceField, setEvidenceField] = useState<string | null>(null);
  const fieldsConfig = useAppSelector((state) => state.config.fieldsConfig);
  const currentSite = useAppSelector((state) => state.site.currentSite);
  const transcripts = useAppSelector((state) => state.transcript.transcripts);
  const currentSession = useAppSelector((state) => state.session.currentSession);

  // Derived (not a prop): a field is answered when it has a value that is not
  // pending. Memoized so the Set identity stays stable across renders.
  const answeredFields = useMemo(() => {
    const answered = new Set<string>();
    getFieldOrder(fieldsConfig).forEach((fieldName) => {
      const value = extractedFields[fieldName];
      if (value && !isFieldPending(value)) answered.add(fieldName);
    });
    return answered;
  }, [fieldsConfig, extractedFields]);

  const recentlyChanged = useFieldChangeHighlight(answeredFields);

  // Build set of fields that have evidence. Human-overridden fields are included — the
  // evidence modal shows both the LLM answer and the user override, so it stays useful.
  const fieldsWithEvidence = useMemo(() => {
    const result = new Set<string>();

    // Check transcript evidence
    for (const t of transcripts) {
      if (t.evidence) {
        for (const fieldName of Object.keys(t.evidence)) {
          if (t.evidence[fieldName]?.length > 0) {
            result.add(fieldName);
          }
        }
      }
    }

    // Check notes evidence
    const notesEvidence = currentSession?.notes_evidence || {};
    for (const fieldName of Object.keys(notesEvidence)) {
      if (notesEvidence[fieldName]?.length > 0) {
        result.add(fieldName);
      }
    }

    return result;
  }, [transcripts, currentSession]);

  useEffect(() => {
    if (!isEditMode) {
      setEditedFields(extractedFields);
    }
  }, [extractedFields, isEditMode]);

  const handleFieldChange = (fieldName: string, value: FieldValue) => {
    // Re-seeds grouped child fields when their parent changes (shared with the validation /
    // evidence-modal editors).
    setEditedFields(applyGroupedChildInit(fieldsConfig, fieldName, value, editedFields));
  };

  const handleSaveClick = async () => {
    if (onSave) {
      const fieldsToSave: Record<string, FieldValue> = {};
      Object.entries(editedFields).forEach(([key, value]) => {
        if (value === undefined) return;
        const originalValue = extractedFields[key];
        if (fieldValuesEqual(value, originalValue)) return;
        if (value === '<UNKNOWN>' && (!originalValue || originalValue === '<UNKNOWN>')) return;
        fieldsToSave[key] = value;
      });
      await onSave(fieldsToSave);
    }
  };

  const handleCancelClick = () => {
    setEditedFields(extractedFields);
    setSearchQueries({});
    if (onCancel) onCancel();
  };

  const formatDisplayValue = (value: FieldValue | undefined): string => {
    if (!value) return '';
    if (value === '<UNKNOWN>') return '';
    if (value === '<NOT_APPLICABLE>' || value === 'Not applicable') return 'Not applicable';
    if (isGroupedFieldValue(value)) {
      const entries = getMeaningfulGroupEntries(value);
      if (entries.length === 0) return '';
      if (entries.length === 1) {
        const [, vals] = entries[0];
        const filtered = Array.isArray(vals) ? vals.filter(v => v !== '<UNKNOWN>') : [String(vals)];
        return filtered.join(', ');
      }
      return entries
        .map(([parent, vals]) => {
          const display = Array.isArray(vals)
            ? vals.filter(v => v !== '<UNKNOWN>').join(', ')
            : String(vals);
          return display ? `${parent}: ${display}` : null;
        })
        .filter(Boolean)
        .join('; ');
    }
    if (Array.isArray(value)) {
      if (value.length === 0 || (value.length === 1 && value[0] === '<UNKNOWN>')) return '';
      if (value.length === 1 && (value[0] === '<NOT_APPLICABLE>' || value[0] === 'Not applicable')) return 'Not applicable';
      return value.filter(v => v !== '<UNKNOWN>' && v !== '<NOT_APPLICABLE>').join(', ');
    }
    return value;
  };

  const allFieldNames = fieldNamesProp || getFieldOrder(fieldsConfig);

  const hasFormatErrors = isEditMode && allFieldNames.some((fieldName) => {
    const fieldDef = getFieldDefinition(fieldsConfig, fieldName);
    const format = getFieldFormatPlaceholder(fieldDef?.description);
    if (!format) return false;
    const value = editedFields[fieldName];
    if (!value || value === '<UNKNOWN>' || typeof value !== 'string') return false;
    return !validateFieldFormat(value, format);
  });

  // Sort: unanswered first, extracting second, answered third, N/A bottom
  const sortedFieldNames = [...allFieldNames].sort((a, b) => {
    const aAnswered = answeredFields.has(a);
    const bAnswered = answeredFields.has(b);
    const aExtracting = isFieldExtracting(extractedFields[a]);
    const bExtracting = isFieldExtracting(extractedFields[b]);
    const aNa = isFieldNotApplicable(extractedFields[a]);
    const bNa = isFieldNotApplicable(extractedFields[b]);

    const getOrder = (answered: boolean, extracting: boolean, na: boolean) => {
      if (!answered && !extracting) return 0; // unanswered first
      if (extracting) return 1;               // extracting second
      if (answered && !na) return 2;          // answered third
      return 3;                               // N/A bottom
    };

    return getOrder(aAnswered, aExtracting, aNa) - getOrder(bAnswered, bExtracting, bNa);
  });

  // Second pass: move child fields to be right after their parent
  const reorderedFieldNames = [...sortedFieldNames];
  const moved = new Set<string>();
  for (let i = 0; i < reorderedFieldNames.length; i++) {
    const fieldName = reorderedFieldNames[i];
    if (moved.has(fieldName)) continue;
    const fieldDef = getFieldDefinition(fieldsConfig, fieldName);
    if (!fieldDef?.depends_on) continue;

    const parentIdx = reorderedFieldNames.indexOf(fieldDef.depends_on);
    if (parentIdx === -1) continue;

    if (parentIdx > i) {
      // Child is before parent — move child to right after parent
      let insertIdx = parentIdx; // after splice, parent shifts left by 1, so parentIdx becomes right after
      reorderedFieldNames.splice(i, 1);
      // Find insertion point after parent and its already-moved siblings
      while (insertIdx < reorderedFieldNames.length) {
        const sibDef = getFieldDefinition(fieldsConfig, reorderedFieldNames[insertIdx]);
        if (sibDef?.depends_on === fieldDef.depends_on) {
          insertIdx++;
        } else {
          break;
        }
      }
      reorderedFieldNames.splice(insertIdx, 0, fieldName);
      moved.add(fieldName);
      i--; // re-check this position
      continue;
    }

    // Child is after parent — move closer if needed
    let insertIdx = parentIdx + 1;
    while (insertIdx < i) {
      const sibDef = getFieldDefinition(fieldsConfig, reorderedFieldNames[insertIdx]);
      if (sibDef?.depends_on === fieldDef.depends_on) {
        insertIdx++;
      } else {
        break;
      }
    }

    if (insertIdx < i) {
      reorderedFieldNames.splice(i, 1);
      reorderedFieldNames.splice(insertIdx, 0, fieldName);
      moved.add(fieldName);
      i--; // re-check this position
    }
  }

  // Root of a field's dependency chain (walk depends_on to the top). Adjacent fields
  // sharing a root form one visual group, so the separator between them is dropped.
  const groupRootOf = (fieldName: string): string => {
    let cur = fieldName;
    let def = getFieldDefinition(fieldsConfig, cur);
    while (def?.depends_on && getFieldDefinition(fieldsConfig, def.depends_on)) {
      cur = def.depends_on;
      def = getFieldDefinition(fieldsConfig, cur);
    }
    return cur;
  };

  const renderFieldItem = (fieldName: string, hideSeparator: boolean) => {
    const label = getFieldLabel(fieldsConfig, fieldName);
    const fieldDef = getFieldDefinition(fieldsConfig, fieldName);
    const isMultiselect = fieldDef?.type === 'multiselect';
    const isSingleSelect = fieldDef?.type === 'select';
    const value = isEditMode ? editedFields[fieldName] : extractedFields[fieldName];
    const displayValue = formatDisplayValue(value);
    const isNotApplicable = isFieldNotApplicable(value);
    const isAnswered = answeredFields.has(fieldName);
    const isExtracting = isFieldExtracting(value);
    const searchQuery = searchQueries[fieldName] || '';
    const isHighlighted = recentlyChanged.has(fieldName);

    const selectedValues: string[] = isGroupedFieldValue(value)
      ? ([] as string[]).concat(...Object.values(value))
      : Array.isArray(value)
        ? value.filter(v => v !== '<UNKNOWN>' && v !== '<NOT_APPLICABLE>')
        : (value && value !== '<UNKNOWN>' && value !== '<NOT_APPLICABLE>' ? [value] : []);

    // Prefer the current site's captured options over the profile defaults.
    const { options: resolvedOptions, hierarchyMapping: resolvedHierarchyMapping } = resolveFieldOptions(fieldDef, currentSite);
    // Keep any already-selected value selectable even if it isn't in the resolved list.
    const resolvedValues = new Set(resolvedOptions.map(o => o.value));
    const optionsWithSelected = [
      ...resolvedOptions,
      ...selectedValues.filter(v => !resolvedValues.has(v)).map(v => ({ value: v, label: v })),
    ];

    const filteredOptions = optionsWithSelected.length
      ? sortOptionsSelectedFirst(
          optionsWithSelected.filter(opt =>
            opt.value !== '<UNKNOWN>' &&
            opt.value !== '<NOT_APPLICABLE>' &&
            opt.label.toLowerCase().includes(searchQuery.toLowerCase())
          ),
          selectedValues
        )
      : [];

    const hasNotApplicableOption = fieldDef?.options?.some(opt => opt.value === '<NOT_APPLICABLE>');

    let parentFieldValues: string[] | undefined;
    let isChildLocked = false;
    if (fieldDef?.depends_on) {
      const parentValue = isEditMode ? editedFields[fieldDef.depends_on] : extractedFields[fieldDef.depends_on];
      if (isGroupedFieldValue(parentValue)) {
        parentFieldValues = ([] as string[]).concat(
          ...Object.values(parentValue)
        ).filter(v => v && v !== '<UNKNOWN>');
      } else if (Array.isArray(parentValue)) {
        parentFieldValues = parentValue.filter(v => v && v !== '<UNKNOWN>');
      } else if (typeof parentValue === 'string' && parentValue && parentValue !== '<UNKNOWN>') {
        parentFieldValues = [parentValue];
      }
      isChildLocked = !parentFieldValues || parentFieldValues.length === 0;

      // If depends_on_value is set, apply branch visibility logic:
      // - In edit mode: always respect parent value (hide child if parent doesn't match)
      // - In view mode: if child is already answered → always show (both parent and child are resolved)
      // - If parent not answered → hide child (don't know which branch)
      // - If parent answered but wrong value → hide child (different branch)
      // - If parent answered and matches → show child unlocked
      if (fieldDef.depends_on_value) {
        const requiredValues = Array.isArray(fieldDef.depends_on_value)
          ? fieldDef.depends_on_value.map((v: string) => v.toLowerCase())
          : [fieldDef.depends_on_value.toLowerCase()];
        const parentMatches = parentFieldValues?.some(v => requiredValues.includes(v.toLowerCase()));

        if (!isEditMode && isAnswered) {
          // View mode: child already has a value — show it regardless of parent state
          isChildLocked = false;
        } else if (!parentMatches) {
          // Parent not set or wrong branch — hide
          return null;
        } else {
          // Parent matches — show child unlocked
          isChildLocked = false;
        }
      }
    }

    const bgColor = isNotApplicable ? FIELD_BG_NOT_SURE : (isAnswered && !isExtracting) ? FIELD_BG_ANSWERED : FIELD_BG_UNANSWERED;
    // Clickable if this field OR any field in its dependency group has evidence
    // (the modal shows the whole group's evidence together).
    const hasEvidence = getFieldGroup(fieldsConfig, fieldName).some((f) => fieldsWithEvidence.has(f));
    const isClickable = hasEvidence && !isEditMode && isAnswered;

    return (
      <Flipped key={fieldName} flipId={`field-${fieldName}`}>
        <div
          onClick={isClickable ? () => setEvidenceField(fieldName) : undefined}
          style={{
            padding: '12px',
            backgroundColor: bgColor,
            fontSize: FONT_SIZE_BODY,
            borderBottom: hideSeparator ? 'none' : '1px solid #e0e0e0',
            transition: TRANSITION_BG_COLOR,
            cursor: isClickable ? 'pointer' : 'default',
            ...getHighlightStyle(isHighlighted),
          }}
        >
        <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
          {/* Status icon */}
          <span style={{ flexShrink: 0, marginTop: '2px', fontSize: '14px', lineHeight: 0 }}>
            {isNotApplicable ? (
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path d="M7 0.5L13.5 12.5H0.5L7 0.5Z" fill={FIELD_ICON_NOT_SURE} />
                <text x="7" y="11" textAnchor="middle" fill="white" fontSize="9" fontWeight="bold">!</text>
              </svg>
            ) : isExtracting ? (
              <Loader size="small" />
            ) : isAnswered ? (
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <circle cx="7" cy="7" r="7" fill={FIELD_ICON_ANSWERED} />
                <path d="M4 7.5L6 9.5L10 5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            ) : (
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <circle cx="7" cy="7" r="6" stroke={FIELD_ICON_UNANSWERED} strokeWidth="2" />
              </svg>
            )}
          </span>
          <div style={{ flex: 1 }}>
            {isEditMode && editable ? (
              <div>
                <span style={{ fontWeight: '600', color: TEXT_COLOR_PRIMARY }}>
                  {label}:
                </span>
                <div style={{ marginTop: '4px' }}>
                  {isChildLocked ? (
                    <span style={{ fontSize: FONT_SIZE_BODY, color: TEXT_COLOR_SECONDARY, fontStyle: 'italic' }}>
                      Select {getFieldLabel(fieldsConfig, fieldDef!.depends_on!)} first
                    </span>
                  ) : (
                    <EditFieldRenderer
                      fieldName={fieldName}
                      value={value}
                      displayValue={displayValue}
                      isMultiselect={isMultiselect}
                      isSingleSelect={isSingleSelect}
                      hasNotApplicableOption={!!hasNotApplicableOption}
                      filteredOptions={filteredOptions}
                      hierarchyMapping={resolvedHierarchyMapping}
                      parentFieldValues={resolvedHierarchyMapping ? parentFieldValues : undefined}
                      searchQuery={searchQuery}
                      placeholder={getFieldFormatPlaceholder(fieldDef?.description)}
                      onFieldChange={handleFieldChange}
                      onSearchChange={(name, query) => setSearchQueries({ ...searchQueries, [name]: query })}
                    />
                  )}
                </div>
              </div>
            ) : isExtracting ? (
              <div style={{ lineHeight: LINE_HEIGHT_BODY }}>
                <span style={{ fontWeight: '600', color: TEXT_COLOR_PRIMARY }}>
                  {label}:
                </span>
                <span style={{ fontSize: '12px', color: TEXT_COLOR_SECONDARY, fontStyle: 'italic', marginLeft: '6px' }}>
                  Information captured, extracting...
                </span>
              </div>
            ) : isGroupedFieldValue(value) && isAnswered && getMeaningfulGroupEntries(value).length > 1 ? (
              <div style={{ lineHeight: LINE_HEIGHT_BODY }}>
                <span style={{ fontWeight: '600', color: TEXT_COLOR_PRIMARY }}>
                  {label}:
                </span>
                <div style={{ marginTop: '4px', paddingLeft: '4px' }}>
                  {getMeaningfulGroupEntries(value).map(([parent, vals]) => {
                    const display = Array.isArray(vals)
                      ? vals.filter(v => v !== '<UNKNOWN>').join(', ')
                      : String(vals);
                    if (!display) return null;
                    return (
                      <div key={parent} style={{ marginBottom: '3px' }}>
                        <span style={{ fontSize: '11px', fontWeight: '500', color: TEXT_COLOR_PRIMARY }}>
                          {parent}:
                        </span>{' '}
                        <span style={{ color: TEXT_COLOR_SECONDARY }}>
                          {display}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <span style={{ lineHeight: LINE_HEIGHT_BODY }}>
                <span style={{ fontWeight: '600', color: TEXT_COLOR_PRIMARY }}>
                  {label}:
                </span>{' '}
                <span style={{ color: TEXT_COLOR_SECONDARY }}>
                  {isAnswered ? displayValue : '--'}
                </span>
              </span>
            )}
          </div>
        </div>
        </div>
      </Flipped>
    );
  };

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '12px',
          backgroundColor: '#fff',
          borderBottom: '1px solid #e0e0e0',
        }}
      >
        <Text type="b400" tag="span"><strong>Extraction Fields</strong></Text>
        {answeredFields.size > 0 && (
          <div>
            {isEditMode ? (
              <div>
                <Button onClick={handleCancelClick} disabled={isSaving} size="small" type="link">
                  Cancel
                </Button>
                <Button onClick={handleSaveClick} disabled={isSaving || hasFormatErrors} size="small" type="link">
                  {isSaving ? 'Saving...' : 'Save'}
                </Button>
              </div>
            ) : (
              <Button onClick={onEdit} size="small" type="link" disabled={!editable}>
                Edit
              </Button>
            )}
          </div>
        )}
      </div>

      {isSaving ? (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Loader size="large" />
        </div>
      ) : (
        <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
          <style>{HIGHLIGHT_PULSE_CSS}</style>
          <Flipper flipKey={reorderedFieldNames.join(',')} spring="gentle">
            {reorderedFieldNames.map((fieldName, idx) => {
              const next = reorderedFieldNames[idx + 1];
              const hideSeparator = !!next && groupRootOf(next) === groupRootOf(fieldName);
              return renderFieldItem(fieldName, hideSeparator);
            })}
          </Flipper>
        </div>
      )}

      {/* Evidence Modal */}
      {evidenceField && extractedFields[evidenceField] && (
        <EvidenceModal open={true} onClose={() => setEvidenceField(null)} fieldName={evidenceField} />
      )}
    </div>
  );
};

export default ExtractedFieldsDisplay;
